import type {
  IntakeDisplayData,
  IntakeResponse,
  PatientVisibleNoteDto,
  PatientQuoteSummary,
  PatientQuotesDashboardResponse,
  PatientTravelCasePlanResponse,
  QuoteTripDay,
} from '@jema-manager/shared';
import { translateBatch } from './googleTranslate';
import {
  normalizeTranslationLocale,
  normalizeTranslationSourceLocale,
  shouldSkipTranslation,
  type TranslationLocale,
  type TranslationSourceLocale,
} from './language';

type Setter = (translated: string) => void;

const collectAndApply = async (
  setters: Setter[],
  texts: string[],
  targetLang: TranslationLocale,
  sourceLang: TranslationSourceLocale
) => {
  if (!texts.length) return;
  const translated = await translateBatch(texts, targetLang, sourceLang);
  translated.forEach((value, index) => setters[index]?.(value));
};

const cloneResponse = (response: IntakeResponse): IntakeResponse => ({
  ...response,
  question: response.question
    ? {
        ...response.question,
        options: response.question.options
          ? [...response.question.options]
          : response.question.options,
      }
    : response.question,
  answer: Array.isArray(response.answer)
    ? [...response.answer]
    : response.answer && typeof response.answer === 'object'
      ? { ...response.answer }
      : response.answer,
});

const collectStringValue = (
  value: string | undefined | null,
  texts: string[],
  setters: Setter[],
  setValue: (translated: string) => void
) => {
  if (!value) return;
  texts.push(value);
  setters.push(setValue);
};

const collectFlatObjectStrings = (
  value: Record<string, unknown>,
  texts: string[],
  setters: Setter[],
  setObject: (next: Record<string, unknown>) => void
) => {
  const next = { ...value };
  let changed = false;

  Object.entries(next).forEach(([key, item]) => {
    if (typeof item === 'string' && item) {
      texts.push(item);
      setters.push(translated => {
        next[key] = translated;
        setObject(next);
      });
      changed = true;
      return;
    }

    if (Array.isArray(item) && item.every(entry => typeof entry === 'string')) {
      const nextItems = [...item];
      item.forEach((entry, index) => {
        if (!entry) return;
        texts.push(entry);
        setters.push(translated => {
          nextItems[index] = translated;
          next[key] = nextItems;
          setObject(next);
        });
        changed = true;
      });
    }
  });

  if (changed) {
    setObject(next);
  }
};

export const translateIntakeResponses = async (
  responses: IntakeResponse[],
  targetLang: string,
  sourceLang?: string
): Promise<IntakeResponse[]> => {
  if (!responses.length) return responses;

  const normalizedTarget = normalizeTranslationLocale(targetLang);
  const normalizedSource = normalizeTranslationSourceLocale(sourceLang);
  if (shouldSkipTranslation(normalizedSource, normalizedTarget)) return responses;

  const cloned = responses.map(cloneResponse);
  const texts: string[] = [];
  const setters: Setter[] = [];

  cloned.forEach(response => {
    if (response.question?.prompt) {
      collectStringValue(response.question.prompt, texts, setters, value => {
        if (response.question) response.question.prompt = value;
      });
    }

    if (Array.isArray(response.question?.options)) {
      response.question?.options?.forEach((option, optionIndex) => {
        collectStringValue(option, texts, setters, value => {
          if (response.question?.options) response.question.options[optionIndex] = value;
        });
      });
    }

    if (typeof response.answer === 'string' && response.answer) {
      collectStringValue(response.answer, texts, setters, value => {
        response.answer = value;
      });
      return;
    }

    if (Array.isArray(response.answer) && response.answer.every(item => typeof item === 'string')) {
      response.answer.forEach((value, answerIndex) => {
        collectStringValue(value, texts, setters, translated => {
          if (Array.isArray(response.answer)) {
            response.answer[answerIndex] = translated;
          }
        });
      });
      return;
    }

    if (response.answer && !Array.isArray(response.answer) && typeof response.answer === 'object') {
      collectFlatObjectStrings(response.answer as Record<string, unknown>, texts, setters, next => {
        response.answer = next;
      });
    }
  });

  await collectAndApply(setters, texts, normalizedTarget, normalizedSource);
  return cloned;
};

export const translateIntakeDisplayData = async (
  data: IntakeDisplayData,
  targetLang: string,
  sourceLang?: string
): Promise<IntakeDisplayData> => {
  const normalizedTarget = normalizeTranslationLocale(targetLang);
  const normalizedSource = normalizeTranslationSourceLocale(sourceLang);
  if (shouldSkipTranslation(normalizedSource, normalizedTarget)) return data;

  const [translatedResponses, translatedNotes] = await Promise.all([
    translateIntakeResponses(data.responses ?? [], normalizedTarget, normalizedSource),
    translatePatientVisibleNotes(data.jemaNotes, normalizedTarget, normalizedSource),
  ]);

  const clonedMedia = (data.media ?? []).map(media => ({
    ...media,
    metadata: media.metadata ? { ...media.metadata } : media.metadata,
  }));
  const texts: string[] = [];
  const setters: Setter[] = [];

  clonedMedia.forEach(media => {
    collectStringValue(media.originalFilename, texts, setters, value => {
      media.originalFilename = value;
    });

    if (media.metadata && typeof media.metadata === 'object') {
      collectFlatObjectStrings(media.metadata, texts, setters, next => {
        media.metadata = next;
      });
    }
  });

  await collectAndApply(setters, texts, normalizedTarget, normalizedSource);

  return {
    ...data,
    responses: translatedResponses,
    media: clonedMedia,
    jemaNotes: translatedNotes,
  };
};

export const translatePatientVisibleNotes = async (
  notes: PatientVisibleNoteDto[] | undefined | null,
  targetLang: string,
  sourceLang?: string
): Promise<PatientVisibleNoteDto[]> => {
  if (!notes?.length) return notes ?? [];

  const normalizedTarget = normalizeTranslationLocale(targetLang);
  const normalizedSource = normalizeTranslationSourceLocale(sourceLang);
  if (shouldSkipTranslation(normalizedSource, normalizedTarget)) return notes;

  const cloned = notes.map(note => ({ ...note }));
  const texts: string[] = [];
  const setters: Setter[] = [];

  cloned.forEach(note => {
    if (!note.body) return;
    texts.push(note.body);
    setters.push(value => {
      note.body = value;
    });
  });

  await collectAndApply(setters, texts, normalizedTarget, normalizedSource);
  return cloned;
};

const translateQuoteTripDays = async (
  days: QuoteTripDay[] | undefined,
  targetLang: TranslationLocale,
  sourceLang: TranslationSourceLocale
): Promise<QuoteTripDay[] | undefined> => {
  if (!Array.isArray(days) || !days.length) return days;

  const cloned = days.map(day => ({
    ...day,
    tags: Array.isArray(day.tags) ? [...day.tags] : day.tags,
  }));
  const texts: string[] = [];
  const setters: Setter[] = [];

  cloned.forEach(day => {
    if (day.notes) {
      texts.push(day.notes);
      setters.push(value => {
        day.notes = value;
      });
    }

    if (Array.isArray(day.tags)) {
      day.tags.forEach((tag, index) => {
        if (!tag) return;
        texts.push(tag);
        setters.push(value => {
          if (Array.isArray(day.tags)) day.tags[index] = value;
        });
      });
    }
  });

  await collectAndApply(setters, texts, targetLang, sourceLang);
  return cloned;
};

export const translatePatientQuoteSummary = async (
  quote: PatientQuoteSummary,
  targetLang: string,
  sourceLang?: string
): Promise<PatientQuoteSummary> => {
  const normalizedTarget = normalizeTranslationLocale(targetLang);
  const normalizedSource = normalizeTranslationSourceLocale(sourceLang);

  if (shouldSkipTranslation(normalizedSource, normalizedTarget)) return quote;

  const cloned: PatientQuoteSummary = {
    ...quote,
    treatmentPlan:
      quote.treatmentPlan && !Array.isArray(quote.treatmentPlan)
        ? {
            ...quote.treatmentPlan,
            procedures: quote.treatmentPlan.procedures.map(procedure => ({ ...procedure })),
            travel: {
              ...quote.treatmentPlan.travel,
              trips: quote.treatmentPlan.travel.trips.map(trip => ({
                ...trip,
                days: trip.days ? [...trip.days] : trip.days,
              })),
            },
          }
        : quote.treatmentPlan,
  };

  const texts: string[] = [];
  const setters: Setter[] = [];

  if (cloned.treatmentPlan && !Array.isArray(cloned.treatmentPlan)) {
    cloned.treatmentPlan.procedures.forEach(procedure => {
      if (procedure.name) {
        texts.push(procedure.name);
        setters.push(value => {
          procedure.name = value;
        });
      }

      if (procedure.description) {
        texts.push(procedure.description);
        setters.push(value => {
          procedure.description = value;
        });
      }
    });

    for (const trip of cloned.treatmentPlan.travel.trips) {
      trip.days = await translateQuoteTripDays(trip.days, normalizedTarget, normalizedSource);
    }
  }

  await collectAndApply(setters, texts, normalizedTarget, normalizedSource);
  return cloned;
};

export const translatePatientQuotesDashboard = async (
  dashboard: PatientQuotesDashboardResponse,
  targetLang: string,
  sourceLang?: string
): Promise<PatientQuotesDashboardResponse> => {
  const normalizedTarget = normalizeTranslationLocale(targetLang);
  const normalizedSource = normalizeTranslationSourceLocale(sourceLang);

  if (shouldSkipTranslation(normalizedSource, normalizedTarget)) return dashboard;

  const clone: PatientQuotesDashboardResponse = {
    ...dashboard,
    unaccepted: dashboard.unaccepted.map(group => ({
      ...group,
      quotes: group.quotes.map(quote => ({ ...quote, procedureTags: [...quote.procedureTags] })),
    })),
    accepted: dashboard.accepted.map(card => ({
      ...card,
      quote: { ...card.quote, procedureTags: [...card.quote.procedureTags] },
    })),
    travelPlanned: dashboard.travelPlanned.map(card => ({
      ...card,
      serviceRequests: card.serviceRequests.map(request => ({
        ...request,
        procedureTags: [...request.procedureTags],
      })),
    })),
    archived: dashboard.archived.map(card => ({
      ...card,
      quote: { ...card.quote, procedureTags: [...card.quote.procedureTags] },
    })),
  };

  const texts: string[] = [];
  const setters: Setter[] = [];

  const collectTags = (tags: string[], setTag: (index: number, value: string) => void) => {
    tags.forEach((tag, index) => {
      if (!tag) return;
      texts.push(tag);
      setters.push(value => setTag(index, value));
    });
  };

  clone.unaccepted.forEach(group => {
    group.quotes.forEach(quote => {
      collectTags(quote.procedureTags, (index, value) => {
        quote.procedureTags[index] = value;
      });
    });
  });

  clone.accepted.forEach(card => {
    collectTags(card.quote.procedureTags, (index, value) => {
      card.quote.procedureTags[index] = value;
    });
  });

  clone.travelPlanned.forEach(card => {
    card.serviceRequests.forEach(request => {
      collectTags(request.procedureTags, (index, value) => {
        request.procedureTags[index] = value;
      });
    });
  });

  clone.archived.forEach(card => {
    collectTags(card.quote.procedureTags, (index, value) => {
      card.quote.procedureTags[index] = value;
    });
  });

  await collectAndApply(setters, texts, normalizedTarget, normalizedSource);
  return clone;
};

export const translatePatientTravelCasePlan = async (
  plan: PatientTravelCasePlanResponse,
  targetLang: string,
  sourceLang?: string
): Promise<PatientTravelCasePlanResponse> => {
  const normalizedTarget = normalizeTranslationLocale(targetLang);
  const normalizedSource = normalizeTranslationSourceLocale(sourceLang);

  if (shouldSkipTranslation(normalizedSource, normalizedTarget)) return plan;

  const clone: PatientTravelCasePlanResponse = {
    ...plan,
    days: (plan.days ?? []).map(day => ({
      ...day,
      tags: Array.isArray(day.tags) ? [...day.tags] : day.tags,
      items: (day.items ?? []).map(item => ({ ...item })),
    })),
    globalFees: (plan.globalFees ?? []).map(fee => ({ ...fee })),
  };

  const texts: string[] = [];
  const setters: Setter[] = [];

  clone.days.forEach(day => {
    if (day.notes) {
      texts.push(day.notes);
      setters.push(value => {
        day.notes = value;
      });
    }

    if (Array.isArray(day.tags)) {
      day.tags.forEach((tag, index) => {
        if (!tag) return;
        texts.push(tag);
        setters.push(value => {
          if (Array.isArray(day.tags)) day.tags[index] = value;
        });
      });
    }

    day.items.forEach((item, itemIndex) => {
      if (item.type !== 'fee') return;
      if (item.label) {
        texts.push(item.label);
        setters.push(value => {
          const feeItem = day.items[itemIndex];
          if (feeItem && feeItem.type === 'fee') {
            feeItem.label = value;
          }
        });
      }
      if (item.description) {
        texts.push(item.description);
        setters.push(value => {
          const feeItem = day.items[itemIndex];
          if (feeItem && feeItem.type === 'fee') {
            feeItem.description = value;
          }
        });
      }
    });
  });

  clone.globalFees.forEach((fee, feeIndex) => {
    if (fee.label) {
      texts.push(fee.label);
      setters.push(value => {
        clone.globalFees[feeIndex].label = value;
      });
    }
    if (fee.description) {
      texts.push(fee.description);
      setters.push(value => {
        clone.globalFees[feeIndex].description = value;
      });
    }
  });

  await collectAndApply(setters, texts, normalizedTarget, normalizedSource);
  return clone;
};
